<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="modelValue"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        @mousedown.self="closeOnBackdrop"
      >
        <div
          ref="modalContent"
          class="max-w-full max-h-[90vh] p-0 my-4 flex flex-col relative"
          :class="sizeClass[size]"
        >
          <button
            v-if="!$slots.header"
            @click="$emit('update:modelValue', false)"
            :title="$t('common.close')"
            type="button"
            class="cursor-pointer absolute bottom-full right-0 mb-2 z-10 bg-surface-light rounded-full p-2 shadow hover:bg-surface-grey transition"
          >
            <CloseIcon class="w-6 h-6" />
          </button>

          <div
            class="w-full rounded-md shadow-lg overflow-auto flex flex-col"
            :class="backgroundClass[background]"
          >
            <div
              class="p-6 text-lg font-semibold border-b border-surface-grey"
              v-if="$slots.header"
            >
              <slot name="header" />

              <button
                @click="$emit('update:modelValue', false)"
                :title="$t('common.close')"
                type="button"
                class="cursor-pointer absolute top-4 right-4 z-10 p-2 hover:opacity-50 transition"
                v-if="!mustConfirm"
              >
                <CloseIcon class="w-6 h-6" />
              </button>
            </div>

            <slot />

            <div
              class="p-6 border-t border-surface-grey bg-surface-grey-light"
              v-if="$slots.footer"
            >
              <slot name="footer" />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref } from 'vue';

  import CloseIcon from '../icons/CloseIcon.vue';

  const props = withDefaults(
    defineProps<{
      modelValue: boolean;
      size?: 'sm' | 'md' | 'lg' | 'fit';
      background?: 'white' | 'transparent';
      mustConfirm?: boolean;
    }>(),
    {
      modelValue: undefined,
      size: 'md',
      background: 'white',
      mustConfirm: false,
    }
  );

  const sizeClass = {
    sm: 'w-75',
    md: 'w-150',
    lg: 'w-[90vw]',
    fit: 'w-fit max-w-[min(90vw,100%)]',
  };

  const backgroundClass = {
    white: 'bg-white',
    transparent: 'bg-transparent shadow-none',
  };

  const emit = defineEmits(['update:modelValue']);

  const modalContent = ref<HTMLElement | null>(null);

  function closeOnBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget && !props.mustConfirm) {
      emit('update:modelValue', false);
    }
  }

  onMounted(() => {
    window.addEventListener('keydown', onEscClose);
  });
  onBeforeUnmount(() => {
    window.removeEventListener('keydown', onEscClose);
  });

  function onEscClose(e: KeyboardEvent) {
    if (e.key === 'Escape' && props.modelValue && !props.mustConfirm) {
      emit('update:modelValue', false);
    }
  }
</script>
